import { Action, SagaIterator } from 'redux-saga';
import { all, call, put } from 'redux-saga/effects';
import { DevBoxAddOnActionState } from '../../../constants/add-ons';
import { listDevBoxAddOns, ListDevBoxAddOnsResponse } from '../../../data/services/data-plane-api/add-ons';
import { DevBoxAddOn } from '../../../models/add-ons';
import { ClientError, FailureOperation, isFailureResponse } from '../../../models/common';
import {
    listDevBoxAddOns as listAddOnsActionCreator,
    listDevBoxAddOnsError,
    listDevBoxAddOnsFailed,
    listDevBoxAddOnsSuccess,
    pollNonTerminalDevBoxAddOn,
} from '../../actions/add-ons/add-ons-action-creators';
import { ListDevBoxAddOnsAction } from '../../actions/add-ons/add-ons-actions';
import { getAccessToken } from '../../actions/identity/identity-action-creators';
import { GetAccessTokenForDevCenterDataPlanePayload } from '../../actions/identity/identity-actions';
import { createSagaError } from '../../effects/create-saga-error';
import { putAndAwait } from '../../effects/put-and-await';
import { resolveAction } from '../../effects/resolve-action';
import { takeEvery } from '../../effects/take';

const getPendingOperationsActions = (id: string, devBoxAddOn: DevBoxAddOn, activityId?: string): Action[] => {
    const actions = [];
    const meta = activityId ? { activityId } : undefined;
    const { actionState, name } = devBoxAddOn;

    switch (actionState) {
        case DevBoxAddOnActionState.Enabling:
            actions.push(pollNonTerminalDevBoxAddOn({ id, name }, meta));
            break;
        default:
            break;
    }

    return actions;
};

export function* listDevBoxAddOnsSaga(action: ListDevBoxAddOnsAction): SagaIterator {
    const { meta, payload } = action;
    const { activityId } = meta ?? {};
    const { id } = payload;

    try {
        const accessToken: string = yield putAndAwait(
            getAccessToken(GetAccessTokenForDevCenterDataPlanePayload(), meta)
        );

        const response: ListDevBoxAddOnsResponse = yield call(listDevBoxAddOns, id, accessToken, activityId);

        if (isFailureResponse(response)) {
            yield put(listDevBoxAddOnsFailed({ failure: response, id }, meta));
            yield resolveAction(action, response);
            return;
        }

        const { data: devBoxAddOns } = response;

        // dispatch actions to track sub resource actions
        // and hydrate additional dev box state
        yield all(
            devBoxAddOns.flatMap((devBoxAddOn) =>
                getPendingOperationsActions(id, devBoxAddOn, activityId).map((action) => put(action))
            )
        );

        yield put(listDevBoxAddOnsSuccess({ id, result: response.data }, meta));
        yield resolveAction(action, response);
    } catch (err) {
        const error: ClientError = yield createSagaError(err, FailureOperation.ListDevBoxAddOns);
        yield put(listDevBoxAddOnsError({ error, id }, meta));
    }
}

export function* listDevBoxAddOnsListenerSaga(): SagaIterator {
    yield takeEvery(listAddOnsActionCreator, listDevBoxAddOnsSaga);
}
